import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Tab = "impressum" | "datenschutz";

export function LegalPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("impressum");

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-gray-200 dark:border-[#1E3A5F]">
        <div className="flex items-center gap-3 px-4 py-3 max-w-2xl mx-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-base font-semibold">Rechtliches</h1>
        </div>

        {/* Tabs */}
        <div className="flex px-4 max-w-2xl mx-auto gap-1 pb-0">
          {(["impressum", "datenschutz"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize",
                tab === t
                  ? "border-vajra-blue text-vajra-blue dark:text-vajra-cyan dark:border-vajra-cyan"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {t === "impressum" ? "Impressum" : "Datenschutz"}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-6 max-w-2xl mx-auto space-y-6 text-sm leading-relaxed text-gray-700 dark:text-muted-foreground">
        {tab === "impressum" ? <Impressum /> : <Datenschutz />}
      </div>
    </div>
  );
}

function Impressum() {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground">Impressum</h2>

      <div className="space-y-1">
        <p className="font-medium text-foreground">Angaben gemäß § 5 DDG</p>
        <p>Jamie und Markus Stein</p>
        <p>Himmelsbörnchen 17</p>
        <p>51588 Nümbrecht</p>
      </div>

      <div className="space-y-1">
        <p className="font-medium text-foreground">Kontakt</p>
        <p>
          E-Mail:{" "}
          <a
            href="mailto:vajra@etik.com"
            className="text-vajra-blue dark:text-vajra-cyan hover:underline"
          >
            vajra@etik.com
          </a>
        </p>
      </div>

      <div className="space-y-1">
        <p className="font-medium text-foreground">Hinweis</p>
        <p>
          VajraSila ist eine nicht-kommerzielle Anwendung zur Unterstützung
          buddhistischer Praxis. Es werden keine Waren oder Dienstleistungen
          verkauft.
        </p>
      </div>
    </div>
  );
}

function Datenschutz() {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-foreground">Datenschutzerklärung</h2>

      <Section title="1. Verantwortliche">
        <p>Jamie und Markus Stein</p>
        <p>Himmelsbörnchen 17, 51588 Nümbrecht</p>
        <p>
          E-Mail:{" "}
          <a href="mailto:vajra@etik.com" className="text-vajra-blue dark:text-vajra-cyan hover:underline">
            vajra@etik.com
          </a>
        </p>
      </Section>

      <Section title="2. Welche Daten wir verarbeiten">
        <p>Bei der Nutzung von VajraSila werden folgende personenbezogene Daten verarbeitet:</p>
        <ul className="list-disc list-inside space-y-1 mt-2 pl-2">
          <li>E-Mail-Adresse (für die Registrierung und Anmeldung)</li>
          <li>Praxis-Einträge (Text, Datum, Zeitslot, Typ)</li>
          <li>Gelübde-Favoriten und Fokus-Einträge</li>
          <li>IP-Adresse (durch den Hosting-Anbieter, temporär)</li>
        </ul>
        <p className="mt-2">
          Praxis-Einträge und Gelübde-Daten werden zusätzlich lokal auf deinem
          Gerät gespeichert (IndexedDB, localStorage), um die Offline-Nutzung
          zu ermöglichen.
        </p>
      </Section>

      <Section title="3. Rechtsgrundlage">
        <p>
          Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b
          DSGVO (Vertragserfüllung), da die Datenspeicherung zur Bereitstellung
          der App-Funktionen notwendig ist.
        </p>
      </Section>

      <Section title="4. Dienstleister">
        <div className="space-y-4">
          <div>
            <p className="font-medium text-foreground">Supabase</p>
            <p>
              Supabase Inc., 970 Trestle Glen Rd, Oakland, CA 94610, USA.
              Supabase stellt die Authentifizierung und Cloud-Datenspeicherung
              bereit. Datenübertragungen in die USA erfolgen auf Basis von
              EU-Standardvertragsklauseln (SCC).
            </p>
            <p>
              Datenschutz:{" "}
              <a
                href="https://supabase.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-vajra-blue dark:text-vajra-cyan hover:underline"
              >
                supabase.com/privacy
              </a>
            </p>
          </div>

          <div>
            <p className="font-medium text-foreground">Vercel</p>
            <p>
              Vercel Inc., 340 S Lemon Ave #4133, Walnut, CA 91789, USA.
              Vercel stellt das Hosting bereit und verarbeitet dabei
              technisch notwendige Zugriffsdaten (u.a. IP-Adressen) temporär.
              Datenübertragungen in die USA erfolgen auf Basis von SCC.
            </p>
            <p>
              Datenschutz:{" "}
              <a
                href="https://vercel.com/legal/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-vajra-blue dark:text-vajra-cyan hover:underline"
              >
                vercel.com/legal/privacy-policy
              </a>
            </p>
          </div>
        </div>
      </Section>

      <Section title="5. Speicherdauer">
        <p>
          Deine Daten werden gespeichert, solange du ein aktives Konto hast.
          Bei Kontolöschung werden alle personenbezogenen Daten aus unseren
          Systemen entfernt. Lokale Gerätedaten kannst du jederzeit über die
          Browser-Einstellungen löschen.
        </p>
      </Section>

      <Section title="6. Deine Rechte">
        <p>Du hast das Recht auf:</p>
        <ul className="list-disc list-inside space-y-1 mt-2 pl-2">
          <li>Auskunft über deine gespeicherten Daten (Art. 15 DSGVO)</li>
          <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
          <li>Löschung deiner Daten (Art. 17 DSGVO)</li>
          <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
          <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
          <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
        </ul>
        <p className="mt-2">
          Für Anfragen wende dich an:{" "}
          <a href="mailto:vajra@etik.com" className="text-vajra-blue dark:text-vajra-cyan hover:underline">
            vajra@etik.com
          </a>
        </p>
      </Section>

      <Section title="7. Beschwerderecht">
        <p>
          Du hast das Recht, dich bei einer Datenschutzaufsichtsbehörde zu
          beschweren. Zuständig für Nordrhein-Westfalen ist die Landesbeauftragte
          für Datenschutz und Informationsfreiheit NRW (LDI NRW),{" "}
          <a
            href="https://www.ldi.nrw.de"
            target="_blank"
            rel="noopener noreferrer"
            className="text-vajra-blue dark:text-vajra-cyan hover:underline"
          >
            www.ldi.nrw.de
          </a>
          .
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="font-medium text-foreground">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
